import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { checkbookService } from '../../services/api';
import { toast } from 'react-toastify';

interface CheckbookFormProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  checkbook?: any;
}

const CheckbookForm: React.FC<CheckbookFormProps> = ({ show, onHide, onSuccess, checkbook }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    bank_name: '',
    account_number: '',
    start_number: '',
    end_number: '',
    status: 'active',
  });

  useEffect(() => {
    if (show) {
      if (checkbook) {
        setFormData({
          name: checkbook.name,
          bank_name: checkbook.bank_name,
          account_number: checkbook.account_number,
          start_number: checkbook.start_number,
          end_number: checkbook.end_number,
          status: checkbook.status,
        });
      } else {
        resetForm();
      }
    }
  }, [show, checkbook]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (parseInt(formData.end_number) <= parseInt(formData.start_number)) {
      toast.error('Le numéro de fin doit être supérieur au numéro de début');
      return;
    }

    setLoading(true);
    try {
      if (checkbook) {
        await checkbookService.update(checkbook.id, formData);
        toast.success('Carnet de chèques modifié avec succès');
      } else {
        await checkbookService.create(formData);
        toast.success('Carnet de chèques créé avec succès');
      }
      onSuccess();
      onHide();
      resetForm();
    } catch (error) {
      toast.error(checkbook ? 'Erreur lors de la modification' : 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      bank_name: '',
      account_number: '',
      start_number: '',
      end_number: '',
      status: 'active',
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{checkbook ? 'Modifier le carnet' : 'Nouveau carnet de chèques'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nom du carnet *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Banque *</Form.Label>
                <Form.Control
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de compte *</Form.Label>
                <Form.Control
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Actif</option>
                  <option value="completed">Terminé</option>
                  <option value="cancelled">Annulé</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de début *</Form.Label>
                <Form.Control
                  type="text"
                  name="start_number"
                  value={formData.start_number}
                  onChange={handleInputChange}
                  disabled={!!checkbook}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de fin *</Form.Label>
                <Form.Control
                  type="text"
                  name="end_number"
                  value={formData.end_number}
                  onChange={handleInputChange}
                  disabled={!!checkbook}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {formData.start_number && formData.end_number && (
            <div className="alert alert-info">
              <strong>Total de chèques:</strong> {parseInt(formData.end_number) - parseInt(formData.start_number) + 1}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (checkbook ? 'Modification...' : 'Création...') : (checkbook ? 'Modifier' : 'Créer')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CheckbookForm;